import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { competenceLabel, competenceShort } from '../../lib/format';
import { Calendar, ChevronDown, Building2, Lock, CalendarRange } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { get } from '../../lib/api';

export default function TopBar({ title, subtitle, action }) {
  const {
    competence, setCompetence,
    companyFilter, setCompanyFilter, companies, closures,
    periodMode, setPeriodMode,
    competenceFrom, setCompetenceFrom,
    competenceTo, setCompetenceTo,
  } = useApp();
  const [allCompetences, setAllCompetences] = useState([]);

  useEffect(() => {
    (async () => {
      // Sempre gerar últimos 18 meses + próximos 6 meses, sempre disponíveis
      const generated = [];
      const today = new Date();
      for (let i = -18; i <= 6; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        generated.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      try {
        const c = await get('/dashboard/competences');
        const set = new Set([...generated, ...c]);
        set.add('2026-04');
        set.add(competence);  // sempre incluir o atual
        const arr = Array.from(set).sort((a, b) => (a < b ? 1 : -1));
        setAllCompetences(arr);
      } catch (e) {
        const set = new Set([...generated, '2026-04', competence]);
        setAllCompetences(Array.from(set).sort((a, b) => (a < b ? 1 : -1)));
      }
    })();
  }, [competence]);

  const selectedCompany = companies.find((c) => c.id === companyFilter);
  const isLocked = closures.some(
    (cl) => cl.competence === competence && cl.locked && (
      (companyFilter === 'all' && cl.company_id === null) ||
      cl.company_id === companyFilter
    )
  );

  return (
    <header className="sticky top-0 z-30 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-900" data-testid="topbar">
      <div className="px-8 py-5 flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-semibold text-zinc-50 tracking-tight" data-testid="page-title">
              {title}
            </h1>
            {isLocked && (
              <span className="pill pill-warning" data-testid="locked-badge">
                <Lock size={11} /> Competência travada
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-[13px] text-zinc-500 mt-1">{subtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Company Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex items-center gap-2 px-3 py-2 bg-zinc-900/60 border border-zinc-800 rounded-md text-[13px] text-zinc-300 hover:bg-zinc-900 hover:border-zinc-700 transition-all"
              data-testid="company-switcher"
            >
              <Building2 size={14} className="text-zinc-500" />
              <span className="font-medium">
                {companyFilter === 'all' ? 'Consolidado' : selectedCompany?.name || '...'}
              </span>
              <ChevronDown size={14} className="text-zinc-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-zinc-200 min-w-[260px]" align="end">
              <DropdownMenuLabel className="text-[11px] text-zinc-500 uppercase tracking-widest">Visão</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setCompanyFilter('all')}
                className="cursor-pointer focus:bg-zinc-800 focus:text-zinc-50"
                data-testid="company-option-all"
              >
                <Building2 size={14} className="mr-2 text-[#D4AF37]" />
                Consolidado do Grupo
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuLabel className="text-[11px] text-zinc-500 uppercase tracking-widest">Empresas</DropdownMenuLabel>
              {companies.map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  onClick={() => setCompanyFilter(c.id)}
                  className="cursor-pointer focus:bg-zinc-800 focus:text-zinc-50"
                  data-testid={`company-option-${c.slug}`}
                >
                  <span
                    className="w-2 h-2 rounded-full mr-2 inline-block"
                    style={{ background: c.color || '#D4AF37' }}
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] text-zinc-500 ml-3 font-mono">{c.ownership_pct.toFixed(2)}%</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Toggle Modo Período (Mês fixo ↔ Período customizado) */}
          <div className="inline-flex items-center bg-zinc-900/60 border border-zinc-800 rounded-md p-0.5" data-testid="period-mode-toggle">
            <button
              type="button"
              onClick={() => setPeriodMode('month')}
              className={`px-2.5 py-1.5 text-[11.5px] rounded transition-all ${
                periodMode === 'month'
                  ? 'bg-amber-950/40 text-amber-200 border border-amber-700/40'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid="period-mode-month"
              title="Filtrar pelo mês exato"
            >
              <Calendar size={12} className="inline mr-1" /> Mês
            </button>
            <button
              type="button"
              onClick={() => setPeriodMode('range')}
              className={`px-2.5 py-1.5 text-[11.5px] rounded transition-all ${
                periodMode === 'range'
                  ? 'bg-amber-950/40 text-amber-200 border border-amber-700/40'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid="period-mode-range"
              title="Filtrar por intervalo customizado (De/Até)"
            >
              <CalendarRange size={12} className="inline mr-1" /> Período
            </button>
          </div>

          {/* Competence Selector — só aparece no modo 'month' */}
          {periodMode === 'month' && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-2 px-3 py-2 bg-zinc-900/60 border border-zinc-800 rounded-md text-[13px] text-zinc-300 hover:bg-zinc-900 hover:border-zinc-700 transition-all"
                data-testid="competence-selector"
              >
                <Calendar size={14} className="text-zinc-500" />
                <span className="font-medium">{competenceLabel(competence)}</span>
                <ChevronDown size={14} className="text-zinc-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-zinc-200 max-h-[300px] overflow-auto" align="end">
                <DropdownMenuLabel className="text-[11px] text-zinc-500 uppercase tracking-widest">Competência</DropdownMenuLabel>
                {allCompetences.map((c) => (
                  <DropdownMenuItem
                    key={c}
                    onClick={() => setCompetence(c)}
                    className="cursor-pointer focus:bg-zinc-800 focus:text-zinc-50"
                    data-testid={`competence-option-${c}`}
                  >
                    <Calendar size={12} className="mr-2 text-zinc-500" />
                    {competenceShort(c)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Range Picker — só aparece no modo 'range' */}
          {periodMode === 'range' && (
            <Popover>
              <PopoverTrigger
                className="flex items-center gap-2 px-3 py-2 bg-zinc-900/60 border border-zinc-800 rounded-md text-[13px] text-zinc-300 hover:bg-zinc-900 hover:border-zinc-700 transition-all"
                data-testid="range-selector"
              >
                <CalendarRange size={14} className="text-zinc-500" />
                <span className="font-medium">
                  {competenceShort(competenceFrom)} <span className="text-zinc-600 mx-1">→</span> {competenceShort(competenceTo)}
                </span>
                <ChevronDown size={14} className="text-zinc-500" />
              </PopoverTrigger>
              <PopoverContent className="bg-zinc-900 border-zinc-800 text-zinc-200 w-72 p-3" align="end">
                <div className="text-[11px] text-zinc-500 uppercase tracking-widest mb-2">Período Customizado</div>
                <div className="space-y-2">
                  <label className="block">
                    <span className="text-[11px] text-zinc-400 uppercase tracking-wider">De</span>
                    <input
                      type="month"
                      value={competenceFrom}
                      onChange={(e) => setCompetenceFrom(e.target.value)}
                      className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-[13px] font-mono text-zinc-100 focus:outline-none focus:border-amber-700/50"
                      data-testid="range-from"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-zinc-400 uppercase tracking-wider">Até</span>
                    <input
                      type="month"
                      value={competenceTo}
                      onChange={(e) => setCompetenceTo(e.target.value)}
                      className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-[13px] font-mono text-zinc-100 focus:outline-none focus:border-amber-700/50"
                      data-testid="range-to"
                    />
                  </label>
                  {competenceFrom > competenceTo && (
                    <div className="text-[11px] text-amber-400 mt-1">
                      Atenção: "De" está depois de "Até".
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {action}
        </div>
      </div>
    </header>
  );
}
